import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { ESTOQUE_PAGE } from "../routes";
import {
  getMassBalanceStoresApi,
  getCurrentBalanceApi,
  addManualOccurrenceApi,
  StoreShort,
  MassBalance,
  AdjustmentType
} from "../api/massBalanceApi";

import { useGoBack } from "../../../hooks/useGoBack";

export default function OcorrenciasPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(ESTOQUE_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [stores, setStores] = useState<StoreShort[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [balance, setBalance] = useState<MassBalance | null>(null);
  const [loading, setLoading] = useState(false);

  // Form state
  const [type, setType] = useState<AdjustmentType>("AVARIA");
  const [quantity, setQuantity] = useState(1);
  const [codAdmin, setCodAdmin] = useState("");
  const [observacao, setObservacao] = useState("");

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    const loadStores = async () => {
      if (!token) return;
      try {
        const data = await getMassBalanceStoresApi(token);
        setStores(data || []);
        if (data && data.length > 0) {
          setSelectedStoreId(data[0].id);
        }
      } catch (err: any) {
        toast.error(err.message || "Erro ao carregar lojas.");
      }
    };
    loadStores();
  }, [token]);

  const loadBalanceOccurrences = async (storeId: string) => {
    if (!token || !storeId) return;
    try {
      setLoading(true);
      const activeBalance = await getCurrentBalanceApi(token, storeId);
      setBalance(activeBalance);
    } catch (err: any) {
      toast.error(err.message || "Erro ao carregar ocorrências.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStoreId) {
      loadBalanceOccurrences(selectedStoreId);
    } else {
      setBalance(null);
    }
  }, [token, selectedStoreId]);

  const handleRegisterOccurrence = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !selectedStoreId) return;

    if (!codAdmin.trim() || quantity <= 0) {
      toast.error("Preencha todos os campos obrigatórios.");
      return;
    }

    try {
      await addManualOccurrenceApi(token, selectedStoreId, {
        codAdmin: codAdmin.trim(),
        type,
        quantity,
        observacao: observacao.trim() || undefined,
      });
      toast.success("Ocorrência lançada com sucesso!");
      setCodAdmin("");
      setQuantity(1);
      setObservacao("");
      loadBalanceOccurrences(selectedStoreId);
    } catch (err: any) {
      toast.error(err.message || "Erro ao lançar ocorrência.");
    }
  };

  if (!token) return null;

  return (
    <div className="estoqueLayout">
      <main className="estoqueMain">
        <div className="estoqueContainer">
          <EcoPageHeader
            crumbs={[{ label: "Estoque", onClick: () => navigate(ESTOQUE_PAGE()) }, { label: "Ocorrências" }]}
            title="Ocorrências de estoque"
            subtitle="Perdas (avarias, vencimentos), devoluções e consumo interno de mercadorias no balanço do dia."
            onBack={goBack}
            backLabel="Voltar para Estoque"
          />

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 24, marginTop: 20 }}>
            {/* Store selector selector */}
            <div className="estoqueFormGroup" style={{ maxWidth: 300 }}>
              <label>Selecione a Loja *</label>
              <select
                className="estoqueSelect"
                value={selectedStoreId}
                onChange={(e) => setSelectedStoreId(e.target.value)}
              >
                <option value="">Selecione...</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nomeFantasia}
                  </option>
                ))}
              </select>
            </div>

            {selectedStoreId && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 24 }}>
                {/* Form to submit manual occurrence */}
                {balance && balance.status !== "CLOSED" ? (
                  <div style={{ background: "var(--eco-ground)", padding: 20, borderRadius: 12, border: "1px solid var(--eco-border)", maxWidth: 600 }}>
                    <h3 style={{ margin: "0 0 16px 0", fontSize: 16, color: "var(--eco-ink)" }}>Lançar Nova Ocorrência Manual</h3>
                    <form onSubmit={handleRegisterOccurrence} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      <div className="estoqueFormGroup">
                        <label>EAN ou Código Admin do Produto *</label>
                        <input
                          type="text"
                          className="estoqueInput"
                          placeholder="Ex: 7891991294508"
                          value={codAdmin}
                          onChange={(e) => setCodAdmin(e.target.value)}
                          required
                        />
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        <div className="estoqueFormGroup">
                          <label>Tipo de Ocorrência *</label>
                          <select
                            className="estoqueSelect"
                            value={type}
                            onChange={(e) => setType(e.target.value as AdjustmentType)}
                            required
                          >
                            <option value="AVARIA">Avaria</option>
                            <option value="VENCIMENTO">Vencimento</option>
                            <option value="CONSUMO">Consumo Interno</option>
                            <option value="DEVOLUCAO">Devolução</option>
                          </select>
                        </div>

                        <div className="estoqueFormGroup">
                          <label>Quantidade *</label>
                          <input
                            type="number"
                            className="estoqueInput"
                            min={1}
                            value={quantity}
                            onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                            required
                          />
                        </div>
                      </div>

                      <div className="estoqueFormGroup">
                        <label>Observação (Opcional)</label>
                        <input
                          type="text"
                          className="estoqueInput"
                          placeholder="Justificativa adicional"
                          value={observacao}
                          onChange={(e) => setObservacao(e.target.value)}
                        />
                      </div>

                      <button
                        type="submit"
                        className="estoqueBtn estoqueBtn--primary"
                        style={{ alignSelf: "flex-start" }}
                      >
                        Registrar Ocorrência
                      </button>
                    </form>
                  </div>
                ) : (
                  <div style={{ background: "var(--eco-status-warn-soft)", border: "1px solid #fde68a", padding: 16, borderRadius: 8, color: "var(--eco-status-warn)", fontSize: 14 }}>
                    Não há um ciclo de balanço diário em aberto para esta loja hoje. Inicie um balanço na página principal para registrar ocorrências.
                  </div>
                )}

                {/* List of occurrences already logged */}
                {selectedStoreId && (
                  <div>
                    <h3 style={{ fontSize: 16, color: "var(--eco-ink)", marginBottom: 12 }}>Ocorrências Lançadas Hoje</h3>
                    {loading ? (
                      <div>Carregando...</div>
                    ) : !balance || !balance.occurrences || balance.occurrences.length === 0 ? (
                      <div style={{ color: "var(--eco-muted)", fontSize: 14 }}>Nenhuma ocorrência registrada hoje.</div>
                    ) : (
                      <div className="estoqueTableWrapper">
                        <table className="estoqueTable">
                          <thead>
                            <tr>
                              <th>Produto (Ref/EAN)</th>
                              <th>Tipo</th>
                              <th>Origem</th>
                              <th>Quantidade</th>
                              <th>Justificativa / Obs</th>
                              <th>Horário</th>
                            </tr>
                          </thead>
                          <tbody>
                            {balance.occurrences.map((occ) => (
                              <tr key={occ.id}>
                                <td>{occ.productRef?.descricao || occ.codAdmin || "Desconhecido"}</td>
                                <td>
                                  <span className={`estoqueBadge ${occ.type === "DEVOLUCAO" ? "estoqueBadge--green" : "estoqueBadge--red"}`}>
                                    {occ.type}
                                  </span>
                                </td>
                                <td>{occ.source}</td>
                                <td>{occ.quantity}</td>
                                <td>{occ.observacao || "-"}</td>
                                <td>{new Date(occ.createdAt).toLocaleTimeString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
